import './shopping.css';
import { IonButton, IonContent } from '@ionic/react';
import { useMemo } from 'react';
import { formatDate, formatMoney, formatPrice, storeName } from '../i18n/format';
import { useMessages } from '../i18n/LanguageProvider';
import { PlanPrompt } from '../plan/PlanPrompt';
import { PageHeader } from '../PageHeader';
import { FoodIcon, FoodPlate } from '../pictures/FoodPlate';
import { useAppData } from '../state/AppDataProvider';
import { currentPlan, nextPlan } from '../state/plan-service';
import { shoppingView, type ItemView, type TripView } from './shopping-view';

function ItemRow({ tripId, item }: { tripId: string; item: ItemView }) {
  const { dispatch } = useAppData();
  const { language, t } = useMessages();
  return (
    <li>
      <label className="app-row">
        <input
          type="checkbox"
          className="app-check"
          aria-label={item.name}
          checked={item.checked}
          onChange={() => dispatch({ type: 'toggle-item', tripId, key: item.key })}
        />
        <FoodPlate picture={item.picture} size={32} />
        <span className="app-row-main">
          <span>{item.name}</span>
          <span className="app-item-meta app-muted">
            <span>{item.amount}</span>
            {item.shelfName && <span className="app-small">{item.shelfName}</span>}
          </span>
        </span>
        <span className="app-row-value app-price">
          <span>{formatPrice(item.total, language, item.estimated)}</span>
          {item.onOffer && <span className="app-chip app-chip-offer">{t.onOffer}</span>}
        </span>
      </label>
    </li>
  );
}

function TripItems({ trip }: { trip: TripView }) {
  const { t } = useMessages();
  return trip.groups.map((group) => (
    <div key={group.storeId}>
      <h3 className="app-store">
        <FoodIcon picture="basket" size={24} />
        {storeName(group.storeId)}
      </h3>
      {group.categories.map((entry) => (
        <div key={entry.category}>
          <p className="app-category">{t.categories[entry.category]}</p>
          <ul className="app-rows">
            {entry.items.map((line) => (
              <ItemRow key={line.key} tripId={trip.id} item={line} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  ));
}

function TripSummary({ trip }: { trip: TripView }) {
  const { language, t } = useMessages();
  const total = t.total(formatPrice(trip.total, language, trip.estimated));
  const savings = trip.savings > 0 ? ` · ${t.savings(formatMoney(trip.savings, language))}` : '';
  return (
    <>
      <span className="app-lead">{formatDate(trip.date, language, 'weekday-long')}</span>
      <span className="app-muted">{`${total}${savings}`}</span>
    </>
  );
}

function BoughtButton({ trip }: { trip: TripView }) {
  const { dispatch } = useAppData();
  const { t } = useMessages();
  if (trip.bought) return null;
  return (
    <IonButton expand="block" onClick={() => dispatch({ type: 'mark-bought', tripId: trip.id })}>
      {t.markBought}
    </IonButton>
  );
}

/**
 * DE: Bildschirm „Einkauf“: offene Einkäufe zum Abhaken, erledigte zugeklappt darunter.
 * EN: Shopping screen: open trips to tick off, done ones collapsed below.
 */
export function ShoppingPage() {
  const { data, today } = useAppData();
  const { language, t } = useMessages();
  const view = useMemo(() => shoppingView(data, today, language), [data, today, language]);
  const hasPlan = currentPlan(data, today) ?? nextPlan(data, today);
  return (
    <>
      <PageHeader title={t.tabShopping} />
      <IonContent>
        <div className="app-page">
          {!hasPlan && <PlanPrompt mode="first" />}
          {hasPlan && view.upcoming.length === 0 && <p className="app-hint">{t.allBought}</p>}
          {view.upcoming.map((trip) => (
            <section key={trip.id} className="app-card app-trip">
              <p className="app-trip-summary">
                <TripSummary trip={trip} />
              </p>
              <TripItems trip={trip} />
              <BoughtButton trip={trip} />
            </section>
          ))}
          {view.done.length > 0 && <h2 className="app-section-title">{t.doneTrips}</h2>}
          {view.done.map((trip) => (
            <details key={trip.id} className="app-card app-trip">
              <summary className="app-trip-summary">
                <TripSummary trip={trip} />
              </summary>
              <TripItems trip={trip} />
              <BoughtButton trip={trip} />
            </details>
          ))}
        </div>
      </IonContent>
    </>
  );
}
